// Traduction des échecs d'analyse en message lisible (ticket Chloé du 2026-08-20).
//
// L'edge function écrit `CODE: détail technique` dans `candidates.error_details`
// (cf. `supabase/functions/_shared/analysis-errors.ts`). Ici on ne fait que la
// traduction : un titre court, une explication qui dit à l'utilisateur ce qu'il
// peut FAIRE, et le détail technique gardé pour le survol et la console.
//
// Le reniflage de chaîne reste en repli : les fiches tombées en erreur avant
// l'introduction du préfixe portent encore l'exception brute.

export type AnalysisErrorCode =
  | 'AI_TRUNCATED'
  | 'AI_QUOTA'
  | 'AI_UNAVAILABLE'
  | 'AI_EMPTY'
  | 'AI_TIMEOUT'
  | 'AI_CONFIG'
  | 'DB_CONFLICT'
  | 'UNKNOWN';

export interface AnalysisErrorInfo {
  code: AnalysisErrorCode;
  /** Une ligne, affichable telle quelle sur la carte d'erreur. */
  title: string;
  /** Ce que l'utilisateur peut faire. Vide si rien d'utile à dire. */
  description: string;
  /** true = relancer a une vraie chance d'aboutir. */
  retryable: boolean;
  /** Message d'origine, pour le title= au survol et la console. */
  raw: string;
}

const CATALOG: Record<AnalysisErrorCode, Omit<AnalysisErrorInfo, 'raw' | 'code'>> = {
  AI_TRUNCATED: {
    title: 'Ce CV est trop volumineux pour être analysé en entier',
    description: "Relancez : l'analyse ira à l'essentiel et devrait aboutir.",
    retryable: true,
  },
  AI_QUOTA: {
    title: "Service d'analyse momentanément surchargé",
    description: 'Réessayez dans quelques minutes.',
    retryable: true,
  },
  AI_UNAVAILABLE: {
    title: "Service d'analyse momentanément surchargé",
    description: 'Réessayez dans quelques minutes.',
    retryable: true,
  },
  AI_EMPTY: {
    title: "Ce document n'a pas pu être lu",
    description: 'Scan de mauvaise qualité ou PDF protégé, probablement. Vérifiez le fichier avant de relancer.',
    retryable: true,
  },
  AI_TIMEOUT: {
    title: "L'analyse de ce CV a pris trop de temps",
    description: "Relancez : l'analyse ira à l'essentiel et devrait aboutir.",
    retryable: true,
  },
  AI_CONFIG: {
    title: 'Problème technique de notre côté',
    description: 'Relancer ne changera rien. Signalez-le-nous avec le nom du fichier.',
    retryable: false,
  },
  DB_CONFLICT: {
    title: 'Profil déjà présent',
    description: 'Un talent porte déjà cette adresse e-mail dans votre vivier. Ouvrez la fiche existante plutôt que de la recréer.',
    retryable: false,
  },
  UNKNOWN: {
    title: "L'analyse a échoué",
    description: 'Relancez ; si le problème persiste, signalez-le avec le nom du fichier.',
    retryable: true,
  },
};

const CODES = Object.keys(CATALOG) as AnalysisErrorCode[];

/** Repli pour les lignes écrites avant le préfixe : on renifle l'exception. */
function sniff(raw: string): AnalysisErrorCode {
  if (/Unterminated string|Unexpected end of JSON|JSON at position/i.test(raw)) return 'AI_TRUNCATED';
  if (raw.includes('INVALID_ARGUMENT') || raw.includes('"code":400')) return 'AI_CONFIG';
  if (/\b429\b|RESOURCE_EXHAUSTED/i.test(raw)) return 'AI_QUOTA';
  if (/\b503\b|UNAVAILABLE|high demand/i.test(raw)) return 'AI_UNAVAILABLE';
  if (/réponse vide du modèle|EmptyModelResponse/i.test(raw)) return 'AI_EMPTY';
  if (/\b23505\b|duplicate key value/i.test(raw)) return 'DB_CONFLICT';
  if (/timeout|horloge murale/i.test(raw)) return 'AI_TIMEOUT';
  return 'UNKNOWN';
}

export function parseAnalysisError(rawInput?: string | null): AnalysisErrorInfo {
  const raw = (rawInput ?? '').trim();
  const prefix = CODES.find((c) => raw.startsWith(`${c}:`));
  const code = prefix ?? (raw ? sniff(raw) : 'UNKNOWN');
  return { code, raw, ...CATALOG[code] };
}

/** Libellé d'une ligne de liste compacte — le titre seul, jamais l'exception. */
export function analysisErrorLabel(rawInput?: string | null): string {
  return parseAnalysisError(rawInput).title;
}
